<script setup lang="ts">
import {
	computed,
	inject,
	onBeforeMount,
	ref,
	shallowRef,
	toRefs,
	watch,
} from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import TenStackTable from "@/modules/explorer/components/Metrics/Table/TenStackTable.vue";
import { documentKey } from "@/modules/explorer/ExplorerUtils";
import { addWatcherForRealtime } from "@/modules/explorer/composables/realtime-watcher";
import { TableContext } from "@/modules/explorer/components/Metrics/Table/context";
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import DashboardCardDO = App.Data.DashboardCardDO;
import { useDashboardStore } from "@/modules/dashboards/store";
import { ApiError, Result } from "@/api/base";
import { pivotRowKey } from "@/modules/explorer/components/Metrics/MetricHelper";

const emit = defineEmits([
	"update:isLoading",
	"setCardHeight",
	"update:derivedDocument",
]);

const props = defineProps<{
	maxHeight?: number;
	computedHeight: boolean;
	lastUpdated: number;
	card: DashboardCardDO;
	fetchDataForDataTable: () => Promise<Result<[], ApiError>>;
	fetchTotalsForDataTable: () => Promise<Result<[], ApiError>>;
}>();

const { maxHeight, lastUpdated, computedHeight } = toRefs(props);

const explorerStore = useExplorerStore();
const dataTableResponses = shallowRef<DataTableResponseDTO[]>([]);
const isLoading = ref(false);
const unableToLoadData = ref(false);
const noDataForSelection = ref(false);
const table = ref<TableContext>();
const document = inject(documentKey);
const dataTableTotals = ref<Array<any>>();

const updateLoadingState = (value, inBackground) => {
	if (inBackground) {
		explorerStore.explorerState.isDataLoadingInBackground = value;
	} else {
		isLoading.value = value;
	}
};

const fetchDataForDataTable = async (inBackground: boolean = false) => {
	updateLoadingState(true, inBackground);

	const response = await props.fetchDataForDataTable();

	if (response.isOk()) {
		const apiData = response.value;
		dataTableResponses.value = apiData["data"];
		noDataForSelection.value = apiData["data"][0].data.length === 0;

		const table = document.value.data_tables[0];

		if (
			table.show_total_values &&
			!(
				table.dimensions.length === 1 &&
				table.dimensions[0].type === "hierarchy"
			) &&
			dataTableTotals.value !== null &&
			Object.keys(dataTableTotals.value).length > 0 &&
			pivotRowKey(table) === null
		) {
			dataTableResponses.value[0].data.unshift({
				...dataTableTotals.value,
				isTotalRow: true,
			});
		}

		updateLoadingState(false, inBackground);
		emit("update:derivedDocument", response.value?.derivedDocument);
	}
	if (response.isErr()) {
		if (response.error.status === 422) {
			noDataForSelection.value = true;
		} else {
			unableToLoadData.value = true;
		}
		updateLoadingState(false, inBackground);
	}
};

const fetchTotalsForDataTable = async (inBackground: boolean = false) => {
	updateLoadingState(true, inBackground);

	const response = await props.fetchTotalsForDataTable();
	if (response.isOk()) {
		dataTableTotals.value = response.value;
	}
};

onBeforeMount(() => {
	fetchTotalsForDataTable();
	fetchDataForDataTable();
});
addWatcherForRealtime(
	() => fetchDataForDataTable(true),
	() => fetchTotalsForDataTable(true)
);

watch(isLoading, (newValue) => emit("update:isLoading", newValue));
watch(lastUpdated, async () => await fetchDataForDataTable());
watch([computedHeight, table, lastUpdated], async ([computedHeight]) => {
	if (!computedHeight || !table.value.height) return;

	emit("setCardHeight", table.value.height, table.value.headerHeight);
});

const dashboardMode = computed(
	() => useDashboardStore().dashboardState.dashboard.mode
);
</script>

<template>
	<ten-stack-table
		v-if="
			!unableToLoadData && !noDataForSelection && dataTableResponses.length > 0
		"
		v-model="table"
		:max-height="computedHeight ? null : maxHeight"
		:data="dataTableResponses[0].data"
		:main-hierarchy-label="dataTableResponses[0].mainHierarchyLabel"
		:period-colors="dataTableResponses[0].periodColors"
		:metrics="dataTableResponses[0].metrics"
		:data-table="dataTableResponses[0].dataTableDO"
		:is-dashboard="true"
		:columnsIncludedAfterCustomSql="
			dataTableResponses[0].columnsIncludedAfterCustomSql
		"
		:customSqlRowClasses="dataTableResponses[0].customSqlRowClasses"
		:print-mode="dashboardMode === 'print'"
	/>

	<v-empty-state
		v-if="noDataForSelection"
		class="h-100"
		icon="fa: fa-duotone fa-cloud-binary"
		title="No data for this configuration"
		text="No data available for the period selection and/or selected filters"
	/>

	<v-empty-state
		v-if="unableToLoadData"
		class="h-100"
		icon="fa: fa-duotone fa-wifi-exclamation"
		text="If the issue persists, you may need to update the configuration."
		title="Unable to load data table"
	/>
</template>

<style scoped></style>
